import { useEffect, useRef } from "react";

/**
 * Observa todos os [data-animate] / [data-animate-from] dentro do elemento
 * referenciado e adiciona `is-visible` quando entram na viewport.
 *
 * Elementos que já estão na viewport (ou acima dela) ao montar recebem
 * is-visible imediatamente, sem depender do observer — evita conteúdo
 * invisível ao carregar a página em posição diferente do topo.
 */
export function useScrollReveal() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const elements = section.querySelectorAll<HTMLElement>("[data-animate], [data-animate-from]");
    if (elements.length === 0) return;

    const toObserve: HTMLElement[] = [];

    elements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      // Já visível ou acima do viewport → revela sem animação
      if (rect.top < window.innerHeight) {
        el.classList.add("is-visible");
      } else {
        toObserve.push(el);
      }
    });

    if (toObserve.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -30px 0px" }
    );

    toObserve.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return sectionRef;
}
